import { Switch } from "design-system/atoms/Switch"
import { Card } from "design-system/molecules/Card"
import { Section } from "design-system/molecules/Section"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { guessDefaultLogisticsLocales } from "modules/settings/hooks/amphora/guessDefaultLogisticsLocales"
import { useCommunicationsConfigState } from "modules/settings/hooks/amphora/useCommunicationsConfigState"
import { GoogleIcon } from "modules/shared/icons/google"
import { WhatsappIcon } from "modules/shared/icons/whatsapp"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { AmphoraLogisticsTemplateLocale } from "schema"
import { AmphoraLogisticsWhatsappSetupDialog } from "./AmphoraLogisticsWhatsappSetupDialog"

export const Communications = () => {
  const { t, i18n } = useTranslation()
  const {
    isPending,
    hasWhatsappLogisticsCampaign,
    whatsappDisabled,
    gmailDisabled,
    useWhatsappEnabled,
    useEmailEnabled,
    commitUpdate,
  } = useCommunicationsConfigState()

  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false)
  const [communicationsEnabled, setCommunicationsEnabled] = useState(
    useWhatsappEnabled || useEmailEnabled,
  )

  const defaultWhatsappLocales = useMemo(
    (): AmphoraLogisticsTemplateLocale[] => guessDefaultLogisticsLocales(i18n.language),
    [i18n.language],
  )

  useEffect(() => {
    setCommunicationsEnabled(useWhatsappEnabled || useEmailEnabled)
  }, [useWhatsappEnabled, useEmailEnabled])

  const handleChannelChange = (value: string) => {
    if (value === "whatsapp") {
      if (whatsappDisabled) return
      if (!hasWhatsappLogisticsCampaign) {
        setWhatsappDialogOpen(true)
        return
      }
      commitUpdate({ useWhatsapp: true, useEmail: false })
      return
    }

    if (value === "gmail") {
      if (gmailDisabled) return
      commitUpdate({ useEmail: true, useWhatsapp: false })
    }
  }

  const handleCommunicationsChecked = (checked: boolean) => {
    if (!checked) {
      setCommunicationsEnabled(false)
      if (useWhatsappEnabled || useEmailEnabled) {
        commitUpdate({ useWhatsapp: false, useEmail: false })
      }
      return
    }

    setCommunicationsEnabled(true)
  }

  const selectedChannel = useMemo(() => {
    if (useWhatsappEnabled) return "whatsapp"
    if (useEmailEnabled) return "gmail"
    return undefined
  }, [useEmailEnabled, useWhatsappEnabled])

  const channelOptions = useMemo(
    () =>
      [
        {
          value: "whatsapp",
          disabled: whatsappDisabled,
          label: t("settings.integrations.amphora.communications.whatsapp.title"),
          description: whatsappDisabled
            ? t("settings.integrations.amphora.communications.whatsapp.disabledHint")
            : undefined,
          icon: <WhatsappIcon className="size-4.5" />,
        },
        {
          value: "gmail",
          disabled: gmailDisabled,
          label: t("settings.integrations.amphora.communications.gmail.title"),
          description: gmailDisabled
            ? t("settings.integrations.amphora.communications.gmail.disabledHint")
            : undefined,
          icon: <GoogleIcon className="size-4.5" />,
        },
      ].sort((a, b) => Number(a.disabled) - Number(b.disabled)),
    [gmailDisabled, t, whatsappDisabled],
  )

  return (
    <Card className="p-4 md:p-6">
      <Section
        title={t("settings.integrations.amphora.communications.title")}
        description={t("settings.integrations.amphora.communications.description")}
        action={
          <Switch
            className="data-[state=checked]:bg-success"
            checked={communicationsEnabled}
            disabled={isPending}
            onCheckedChange={handleCommunicationsChecked}
          />
        }
        className="gap-6"
      >
        {communicationsEnabled ? (
          <div className="w-full sm:max-w-xs">
            <Select value={selectedChannel} onValueChange={handleChannelChange}>
              <SelectTrigger disabled={isPending || (whatsappDisabled && gmailDisabled)}>
                <SelectValue
                  placeholder={t("settings.integrations.amphora.communications.integrations.title")}
                />
              </SelectTrigger>
              <SelectContent>
                {channelOptions.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    description={option.description}
                  >
                    <span className="inline-flex items-center gap-3">
                      {option.icon}
                      <span className="text-base font-medium">{option.label}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
        <AmphoraLogisticsWhatsappSetupDialog
          open={whatsappDialogOpen}
          onOpenChange={setWhatsappDialogOpen}
          defaultLocales={defaultWhatsappLocales}
          isPending={isPending}
          onConfirm={(locales) => {
            commitUpdate({ useWhatsapp: true, useEmail: false, templateLanguages: locales })
            setCommunicationsEnabled(true)
            setWhatsappDialogOpen(false)
          }}
        />
      </Section>
    </Card>
  )
}
